import React, { useEffect, useMemo, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { type MetricColumn, metricColumns, metricText } from './metrics';
import type { MetricKey, ProductGroup } from './model';

type Props = {
  title: string;
  dimensionLabel: string;
  rows: Array<ProductGroup>;
  columns: Array<MetricColumn>;
  source: string;
  range: string;
  onDrill?: (row: ProductGroup) => void;
  onCsv: () => void;
  transactionDetail?: boolean;
  /** Measures this table's cards do not return, left out of "Show all measures". */
  unavailable?: ReadonlyArray<MetricKey>;
};

export default function ProductPerformanceTable({
  title,
  dimensionLabel,
  rows,
  columns,
  source,
  range,
  onDrill,
  onCsv,
  transactionDetail,
  unavailable = [],
}: Props) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [allMeasures, setAllMeasures] = useState(false);
  const [sort, setSort] = useState<MetricKey | 'label'>('sales');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => setPage(0), [rows]);
  const visibleColumns = allMeasures ? metricColumns.filter((column) => !unavailable.includes(column.key)) : columns;
  const sorted = useMemo(
    () =>
      [...rows].sort((left, right) => {
        if (sort === 'label') {
          return left.label.localeCompare(right.label) * (direction === 'asc' ? 1 : -1);
        }
        const a = left.metrics[sort];
        const b = right.metrics[sort];
        if (a === null || a === undefined) {
          return b === null || b === undefined ? 0 : 1;
        }
        if (b === null || b === undefined) {
          return -1;
        }
        return (a - b) * (direction === 'asc' ? 1 : -1);
      }),
    [rows, sort, direction],
  );
  const sortBy = (key: MetricKey | 'label') => {
    setSort(key);
    setDirection(sort === key ? (direction === 'asc' ? 'desc' : 'asc') : key === 'label' ? 'asc' : 'desc');
  };

  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: tokens.plateHover }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" variant="h6" sx={{ fontSize: 17 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rows.length.toLocaleString('en-AU')} rows · AUD ·{' '}
            {onDrill ? 'Select a name to filter all sections' : 'Grouped source transactions'}
          </Typography>
        </Box>
        <Button size="small" onClick={onCsv} disabled={!rows.length} sx={{ flexShrink: 0 }}>
          CSV
        </Button>
      </Stack>
      <Box sx={{ px: 2, py: 0.5 }}>
        <FormControlLabel
          control={<Checkbox size="small" checked={allMeasures} onChange={(_, checked) => setAllMeasures(checked)} />}
          label={<Typography variant="caption">Show all measures</Typography>}
        />
      </Box>
      <TableContainer
        sx={{ maxHeight: 550, overflow: 'auto' }}
        tabIndex={0}
        aria-label={`${title}, scroll to see every measure`}
      >
        <Table
          size="small"
          stickyHeader
          aria-label={title}
          sx={{
            minWidth: Math.max(680, visibleColumns.length * 112 + (transactionDetail ? 480 : 200)),
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 190, position: 'sticky', left: 0, zIndex: 4, bgcolor: tokens.surface }}>
                <TableSortLabel active={sort === 'label'} direction={direction} onClick={() => sortBy('label')}>
                  {dimensionLabel}
                </TableSortLabel>
              </TableCell>
              {transactionDetail && <TableCell sx={{ minWidth: 150 }}>Booking number</TableCell>}
              {transactionDetail && <TableCell sx={{ minWidth: 120 }}>Transaction date</TableCell>}
              {visibleColumns.map((column) => (
                <TableCell key={column.key} align="right" sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={sort === column.key} direction={direction} onClick={() => sortBy(column.key)}>
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.slice(page * pageSize, (page + 1) * pageSize).map((row) => (
              <TableRow key={row.key} hover>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: tokens.surface,
                    maxWidth: 260,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {onDrill ? (
                    <Button
                      size="small"
                      onClick={() => onDrill(row)}
                      sx={{
                        px: 0,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        textTransform: 'none',
                        lineHeight: 1.4,
                      }}
                    >
                      {row.label}
                    </Button>
                  ) : (
                    (row.dimensions.vendor ?? row.label)
                  )}
                </TableCell>
                {transactionDetail && (
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{row.dimensions.booking ?? 'Unknown'}</TableCell>
                )}
                {transactionDetail && <TableCell>{row.dimensions.transactionDate ?? 'Unknown'}</TableCell>}
                {visibleColumns.map((column) => {
                  const value = row.metrics[column.key];
                  return (
                    <TableCell
                      key={column.key}
                      align="right"
                      title={`${value ?? 'Unknown'}`}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {metricText(value, column.format)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + (transactionDetail ? 3 : 1)} sx={{ py: 4 }}>
                  No source rows match these filters. Clear a selection or change the dates; missing values remain
                  Unknown.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1))}
        rowsPerPage={pageSize}
        onPageChange={(_, value) => setPage(value)}
        onRowsPerPageChange={(event) => {
          setPageSize(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
        sx={{
          '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: 1 },
          '& .MuiTablePagination-spacer': { display: 'none' },
        }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', px: 2, pb: 1.5, overflowWrap: 'anywhere' }}
      >
        {source} · {range} · Sydney dates. Sums skip missing values; Unknown means no computable value.
      </Typography>
    </Paper>
  );
}
